<template>
    <Dialog v-model:visible="visible" header="Spa Configuration Files" :style="{ width: '600px' }" modal>
        <div class="mb-3 flex flex-column gap-3">
            <div class="flex flex-column sm:flex-row sm:align-items-center gap-2">
                <Tag severity="info" :value="`Config ID: ${configId}`" />
                <SelectButton
                    v-model="kind"
                    :options="kindOptions"
                    optionLabel="label"
                    optionValue="value"
                    :allowEmpty="false"
                    size="small"
                    aria-label="Config file source"
                />
            </div>
        </div>

        <div v-if="loading" class="d-flex justify-center py-8">
            <ProgressSpinner />
        </div>
        <template v-else-if="files.length > 0">
            <DataTable :value="files" size="small" stripedRows>
                <Column field="name" header="File" />
                <Column field="category" header="Category">
                    <template #body="{ data }">
                        <Tag :severity="data.category === 'firmware' ? 'warn' : data.category === 'config' ? 'success' : 'secondary'" :value="data.category" />
                    </template>
                </Column>
                <Column field="size" header="Size">
                    <template #body="{ data }">
                        {{ formatFileSize(data.size) }}
                    </template>
                </Column>
                <Column header="">
                    <template #body="{ data }">
                        <Button icon="pi pi-download" severity="secondary" size="small" text rounded @click="downloadFile(data.name)" />
                    </template>
                </Column>
            </DataTable>
        </template>
        <div v-else class="text-center py-6 text-color-secondary">No files found for this configuration.</div>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useApi } from '@/api'
import { isApiNotFoundError } from '@/lib/utils'
import { type ConfigFileV1, GetSpaConfigFilesV1KindEnum, GetSpaConfigFileDownloadV1KindEnum } from '@geckoal/gecko-api-client'
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import SelectButton from 'primevue/selectbutton'
import ProgressSpinner from 'primevue/progressspinner'

const props = defineProps<{
    configId: string
}>()

const visible = defineModel<boolean>({ default: false })

const emit = defineEmits<{
    error: [message: string]
}>()

const loading = ref(false)
const files = ref<ConfigFileV1[]>([])

// 'source' = raw input artifacts, 'parts' = transformed output artifacts
const kind = ref<GetSpaConfigFilesV1KindEnum>(GetSpaConfigFilesV1KindEnum.Source)

const kindOptions = [
    { label: 'Source', value: GetSpaConfigFilesV1KindEnum.Source },
    { label: 'Parts', value: GetSpaConfigFilesV1KindEnum.Parts }
]

watch(visible, async (isVisible) => {
    if (!isVisible) return
    await loadFiles()
})

// Reload whenever the user switches source/parts while the dialog is open.
watch(kind, async () => {
    if (!visible.value) return
    await loadFiles()
})

const loadFiles = async () => {
    if (!visible.value) return

    loading.value = true
    files.value = []

    try {
        files.value = await useApi().getSpaConfigFilesV1({
            configId: props.configId,
            kind: kind.value
        })
    } catch (error) {
        // A 404 means there are no files for this config — show the empty state rather than erroring out.
        if (isApiNotFoundError(error)) {
            files.value = []
            return
        }
        console.error('Error fetching spa config files', error)
        emit('error', error instanceof Error ? error.message : String(error))
        visible.value = false
    } finally {
        loading.value = false
    }
}

const downloadFile = async (filename: string) => {
    try {
        const response = await useApi().getSpaConfigFileDownloadV1Raw({
            configId: props.configId,
            filename,
            // list + download endpoints expose separate-but-identical kind enums
            kind: kind.value as unknown as GetSpaConfigFileDownloadV1KindEnum
        })

        const blob = await response.raw.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${props.configId}_${filename}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
    } catch (error) {
        console.error(`Error downloading spa config file ${filename}`, error)
        emit('error', error instanceof Error ? error.message : String(error))
    }
}

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
</script>
